import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Plus, Trash2, Pencil, MessagesSquare, Calculator, GitCompare, Sparkles, Loader2, X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CopilotoWidget from "../components/CopilotoWidget";

const REC_COLORS = {
  Recomendado: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Aceptable: "bg-blue-100 text-blue-800 border-blue-300",
  "Revisar decisión": "bg-amber-100 text-amber-800 border-amber-300",
  "No recomendado": "bg-red-100 text-red-800 border-red-300",
};

function Money({ v }) {
  return <span className="tabular font-semibold">{new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0, useGrouping: "always" }).format(v || 0)}</span>;
}

function Analyze() {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [months, setMonths] = useState("0");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const n = parseFloat(String(amount).replace(",", ".")) || 0;
    if (n <= 0) { toast.error("Introduce un importe positivo"); return; }
    setLoading(true);
    try {
      const r = await api.post("/copiloto/analyze", { amount: n, description, financing_months: parseInt(months) || 0 });
      setResult(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-border rounded-lg p-5 space-y-3">
        <div>
          <label className="text-xs text-muted-foreground">¿Qué quieres comprar o decidir?</label>
          <Input data-testid="analyze-description" placeholder="Ej. Móvil nuevo, unas vacaciones, un coche…" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Importe (€) *</label>
            <Input data-testid="analyze-amount" inputMode="decimal" placeholder="1800" value={amount} onChange={(e) => setAmount(e.target.value)} className="text-lg font-semibold tabular" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Financiar en (meses)</label>
            <Input data-testid="analyze-months" inputMode="numeric" placeholder="0 = contado" value={months} onChange={(e) => setMonths(e.target.value)} />
          </div>
        </div>
        <Button data-testid="analyze-submit" onClick={submit} disabled={loading || !amount} className="w-full h-11">
          {loading ? <><Loader2 size={15} className="mr-2 animate-spin" /> Analizando…</> : <><Sparkles size={15} className="mr-2" /> Analizar decisión</>}
        </Button>
      </div>

      {result && <DecisionResult data={result} />}
    </div>
  );
}

function DecisionResult({ data }) {
  const d = data.decision;
  const sim = d.simulation;
  return (
    <div className="border border-border rounded-lg p-5 space-y-4" data-testid="analyze-result">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Índice de Decisión Consciente</div>
          <div className="text-5xl font-extrabold tabular tracking-tighter mt-1">{d.idc}<span className="text-lg text-muted-foreground">/100</span></div>
        </div>
        <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-bold border ${REC_COLORS[d.recommendation] || "bg-secondary"}`}>{d.recommendation}</span>
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-sm leading-relaxed" data-testid="analyze-narrative">
        {data.narrative}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Metric label="Liquidez tras la compra" value={<Money v={sim.post_liquidity} />} sub={sim.post_liquidity_months ? `${sim.post_liquidity_months.toFixed(1)} meses` : ""} />
        <Metric label="Cash flow post" value={<Money v={sim.post_net_cashflow} />} />
        <Metric label="Salud tras la compra" value={`${sim.post_health_score}/100`} sub={`Δ ${sim.health_score_delta >= 0 ? "+" : ""}${sim.health_score_delta}`} />
        <Metric label={sim.debt_created ? "Cuota mensual" : "Deuda generada"} value={sim.debt_created ? <Money v={sim.monthly_installment} /> : "Ninguna"} />
      </div>

      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer font-semibold">Ver desglose del IDC</summary>
        <div className="mt-2 space-y-1">
          {Object.entries(d.components).map(([k, v]) => (
            <div key={k} className="flex justify-between"><span>{k.replace(/_/g, " ")}</span><span className="tabular">{v.toFixed(1)}</span></div>
          ))}
        </div>
      </details>
    </div>
  );
}

function Metric({ label, value, sub }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-background/60">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
      <div className="mt-1 font-semibold text-sm">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Compare() {
  const [question, setQuestion] = useState("");
  const [opts, setOpts] = useState([{ name: "Opción A", amount: "", financing_months: 0 }, { name: "Opción B", amount: "", financing_months: 0 }]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const parsed = opts.map((o) => ({ name: o.name.trim() || "Opción", amount: parseFloat(String(o.amount).replace(",", ".")) || 0, financing_months: parseInt(o.financing_months) || 0 })).filter((o) => o.amount > 0);
    if (parsed.length < 2) { toast.error("Necesitas al menos 2 opciones con importe"); return; }
    setLoading(true);
    try {
      const r = await api.post("/copiloto/compare", { question, options: parsed });
      setResult(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-border rounded-lg p-5 space-y-3">
        <div>
          <label className="text-xs text-muted-foreground">¿Qué estás comparando?</label>
          <Input data-testid="compare-question" placeholder="Ej. Dos hoteles para las vacaciones" value={question} onChange={(e) => setQuestion(e.target.value)} />
        </div>
        {opts.map((o, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_100px_28px] gap-2 items-end" data-testid={`compare-row-${i}`}>
            <div>
              {i === 0 && <label className="text-xs text-muted-foreground">Nombre</label>}
              <Input data-testid={`compare-name-${i}`} value={o.name} onChange={(e) => { const c = [...opts]; c[i].name = e.target.value; setOpts(c); }} />
            </div>
            <div>
              {i === 0 && <label className="text-xs text-muted-foreground">Importe (€)</label>}
              <Input data-testid={`compare-amount-${i}`} inputMode="decimal" value={o.amount} onChange={(e) => { const c = [...opts]; c[i].amount = e.target.value; setOpts(c); }} />
            </div>
            <div>
              {i === 0 && <label className="text-xs text-muted-foreground">Meses</label>}
              <Input data-testid={`compare-months-${i}`} inputMode="numeric" value={o.financing_months} onChange={(e) => { const c = [...opts]; c[i].financing_months = e.target.value; setOpts(c); }} />
            </div>
            <button type="button" onClick={() => setOpts(opts.filter((_, k) => k !== i))} disabled={opts.length <= 2} className="mb-2 text-muted-foreground hover:text-red-600 disabled:opacity-30"><X size={15} /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="compare-add" onClick={() => setOpts([...opts, { name: `Opción ${String.fromCharCode(65 + opts.length)}`, amount: "", financing_months: 0 }])}>
            <Plus size={13} className="mr-1" /> Añadir opción
          </Button>
          <Button data-testid="compare-submit" onClick={submit} disabled={loading} className="ml-auto">
            {loading ? <><Loader2 size={15} className="mr-2 animate-spin" /> Comparando…</> : <><GitCompare size={15} className="mr-2" /> Comparar</>}
          </Button>
        </div>
      </div>

      {result && <ComparisonResult data={result} />}
    </div>
  );
}

function ComparisonResult({ data }) {
  const c = data.comparison;
  return (
    <div className="border border-border rounded-lg p-5 space-y-4" data-testid="compare-result">
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-sm leading-relaxed" data-testid="compare-narrative">
        {data.narrative}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {c.options.map((o, i) => (
          <div key={i} className={`border rounded-lg p-4 ${c.recommended?.name === o.name ? "border-primary shadow-md" : "border-border"}`} data-testid={`compare-option-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading font-bold">{o.name}</span>
              {c.recommended?.name === o.name && <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 rounded">Recomendado</span>}
            </div>
            <div className="text-3xl font-extrabold tabular tracking-tighter">{o.idc}<span className="text-sm text-muted-foreground">/100</span></div>
            <div className={`inline-block px-2 py-0.5 rounded text-xs font-bold border mt-1 ${REC_COLORS[o.recommendation] || "bg-secondary"}`}>{o.recommendation}</div>
            <div className="text-sm mt-3 space-y-0.5">
              <div className="flex justify-between"><span className="text-muted-foreground">Importe</span><Money v={o.amount} /></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Liquidez tras compra</span><Money v={o.simulation.post_liquidity} /></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Salud tras compra</span><span className="tabular font-semibold">{o.simulation.post_health_score}/100</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Chat() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const listRef = useRef(null);

  const loadSessions = () => api.get("/copiloto/sessions").then((r) => setSessions(r.data));
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => {
    if (!active) { setMessages([]); return; }
    api.get(`/copiloto/sessions/${active.id}/messages`).then((r) => setMessages(r.data));
  }, [active]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [messages]);

  const createSession = async () => {
    try {
      const r = await api.post("/copiloto/sessions", { title: "Nueva conversación" });
      await loadSessions();
      setActive(r.data);
      setMessages([]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    const question = text.trim();
    setText("");
    setMessages((prev) => [...prev, { id: "tmp", role: "user", text: question, at: new Date().toISOString() }]);
    try {
      const r = await api.post(`/copiloto/sessions/${active.id}/messages`, { text: question });
      setMessages((prev) => [...prev.filter((m) => m.id !== "tmp"), r.data.user, r.data.assistant]);
      loadSessions();
    } catch (e) { toast.error(apiError(e)); setMessages((prev) => prev.filter((m) => m.id !== "tmp")); }
    finally { setSending(false); }
  };

  const removeSession = async (s) => {
    if (!window.confirm(`¿Eliminar la conversación "${s.title}"?`)) return;
    try {
      await api.delete(`/copiloto/sessions/${s.id}`);
      if (active?.id === s.id) setActive(null);
      loadSessions();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rename = async () => {
    try {
      await api.put(`/copiloto/sessions/${renaming.id}`, { title: renameValue });
      setRenaming(null);
      loadSessions();
      if (active?.id === renaming.id) setActive({ ...active, title: renameValue });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[500px]">
      <div className="border border-border rounded-lg p-3 flex flex-col overflow-hidden">
        <Button data-testid="chat-new-session" onClick={createSession} className="w-full mb-3"><Plus size={14} className="mr-1" /> Nueva conversación</Button>
        <div className="overflow-y-auto flex-1 space-y-1" data-testid="chat-sessions">
          {sessions.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Sin conversaciones aún.</div>}
          {sessions.map((s) => (
            <div key={s.id} className={`group flex items-center gap-1 rounded px-2 py-1.5 cursor-pointer text-sm ${active?.id === s.id ? "bg-secondary" : "hover:bg-secondary/50"}`} onClick={() => setActive(s)} data-testid={`chat-session-${s.id}`}>
              {renaming?.id === s.id ? (
                <Input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename()} onBlur={rename} className="h-6 text-xs" />
              ) : (
                <>
                  <span className="flex-1 truncate">{s.title}</span>
                  <button onClick={(e) => { e.stopPropagation(); setRenaming(s); setRenameValue(s.title); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground"><Pencil size={12} /></button>
                  <button data-testid={`chat-delete-${s.id}`} onClick={(e) => { e.stopPropagation(); removeSession(s); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600"><Trash2 size={12} /></button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-lg flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <MessagesSquare size={40} className="text-muted-foreground/40 mb-3" />
            <h3 className="font-heading font-bold text-lg">Habla con tu copiloto</h3>
            <p className="text-sm text-muted-foreground max-w-sm mt-1">Pregúntale cualquier cosa sobre tu situación financiera. Ejemplo: &quot;¿Puedo permitirme un coche de 25.000 €?&quot;</p>
            <Button className="mt-4" onClick={createSession}><Plus size={14} className="mr-1" /> Empezar</Button>
          </div>
        ) : (
          <>
            <div className="border-b border-border px-4 py-3 font-heading font-bold text-sm">{active.title}</div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Escribe tu primera pregunta para empezar…</div>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                  </div>
                </div>
              ))}
              {sending && <div className="flex justify-start"><div className="bg-secondary rounded-2xl px-4 py-2.5 text-sm"><Loader2 size={14} className="animate-spin inline mr-1" /> Pensando…</div></div>}
            </div>
            <div className="border-t border-border p-3 flex gap-2">
              <Input data-testid="chat-input" placeholder="Pregunta lo que quieras…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !sending && send()} disabled={sending} />
              <Button data-testid="chat-send" onClick={send} disabled={sending || !text.trim()}><Send size={15} /></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Copiloto() {
  const [tab, setTab] = useState("analyze");
  const tabs = [
    { key: "analyze", label: "Analizar decisión", icon: Calculator },
    { key: "compare", label: "Comparar opciones", icon: GitCompare },
    { key: "chat", label: "Chat con tu copiloto", icon: MessagesSquare },
  ];
  return (
    <div className="space-y-6" data-testid="copiloto-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
          <Sparkles size={22} className="text-primary" /> Copiloto Financiero
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Tu asesor personal. Decisiones con criterio usando tus datos reales.</p>
      </div>

      <CopilotoWidget />

      <div className="flex gap-1 border-b border-border">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${tab === t.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "analyze" && <Analyze />}
      {tab === "compare" && <Compare />}
      {tab === "chat" && <Chat />}
    </div>
  );
}
